import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import config from '../../config';
import { fabriquerResolveurDeRattachement } from '../../utils/sitesDeMesure';
/*
 * La règle de rattachement et le périmètre d'un client vivent dans `utils/perimetreClient.js`,
 * partagés avec les quatre écrans du fil d'Ariane : recopier ici ferait diverger ce que le
 * Gestionnaire montre d'un client de ce que l'écran Comptes en dit, sans que rien ne casse.
 */
import {
  resolveursDeRattachement,
  predicatDeRattachement,
  comptesDesSites,
  cleDeCarte,
  cleDeLigne,
  perimetreDuClient as calculerPerimetreDuClient
} from '../../utils/perimetreClient';
import { libellePlateforme } from '../../constants/plateformes';

const API_URL = config.API_URL;

const KanbanContext = createContext(null);

export const useKanban = () => {
  const context = useContext(KanbanContext);
  if (!context) {
    throw new Error('useKanban must be used within a KanbanProvider');
  }
  return context;
};

export const KanbanProvider = ({ children, token, clientFiltre = null }) => {
  // Données
  const [connectors, setConnectors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);

  // États de chargement
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Recherche par colonne
  const [searchQueries, setSearchQueries] = useState({
    connectors: '',
    accounts: '',
    sites: '',
    clients: ''
  });

  // Sélection pour filtrage en cascade
  const [selection, setSelection] = useState({ type: null, id: null });

  // Headers d'authentification
  const authHeaders = {
    headers: { Authorization: `Bearer ${token}` }
  };

  // Charger toutes les données
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [connectorsRes, libraryRes, sitesRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/connectors`, authHeaders),
        axios.get(`${API_URL}/api/library`, authHeaders),
        axios.get(`${API_URL}/api/sites`, authHeaders),
        axios.get(`${API_URL}/api/client-management/clients`, authHeaders)
      ]);

      // Traiter les connecteurs
      const connectorsData = connectorsRes.data?.connectors || connectorsRes.data || [];
      setConnectors(connectorsData);

      // Traiter les comptes (library) - aplatir par provider
      const libraryData = libraryRes.data?.library || libraryRes.data || {};
      const flatAccounts = [];
      Object.entries(libraryData).forEach(([provider, items]) => {
        if (Array.isArray(items)) {
          items.forEach(item => {
            /**
             * Pour Matomo et Piano, `/api/library` met le **numéro de site** dans `id`, et
             * l'identifiant de base sous `databaseId`. Or ce numéro n'est unique que dans une
             * instance : deux comptes de clients différents recevaient le même `id`, donc la
             * même clé React et le même identifiant de glisser-déposer. Les cartes se
             * mélangeaient — on déposait « Espace Locataires » et c'est « Paris en Scène » qui
             * se rattachait.
             *
             * L'identifiant de base fait foi ; le numéro reste lisible sous `sourceId`.
             */
            const identifiant = (provider === 'MATOMO' || provider === 'PIANO_ANALYTICS')
              ? (item.databaseId || item.id)
              : item.id;

            flatAccounts.push({
              ...item,
              id: identifiant,
              provider,
              // Trouver si ce compte est associé à un site
              siteId: null,
              siteName: null
            });
          });
        }
      });

      /**
       * Matomo et Piano numérotent leurs sites **par instance** : « 2 » désigne « Espace
       * Locataires » chez l'un et « Paris en Scène » chez l'autre. Écrire ce numéro nu dans
       * `accountIds`, comme le faisait le glisser-déposer, produit un rattachement qui ne
       * désigne rien de précis — et l'écran affichait alors le premier site trouvé, c'est-à-dire
       * souvent le mauvais.
       *
       * Les résolveurs se construisent sur **tous** les comptes de la plateforme : c'est la
       * comparaison entre instances qui dit si un numéro est ambigu.
       */
      const resolveurs = resolveursDeRattachement(flatAccounts);

      // Traiter les clients d'abord (pour enrichir les sites)
      const clientsData = clientsRes.data?.clients || clientsRes.data || [];

      // Traiter les sites et les enrichir avec le nom du client
      const sitesData = sitesRes.data?.sites || sitesRes.data || [];
      const enrichedSites = sitesData.map(site => {
        const client = clientsData.find(c => c.id === site.clientId);
        return {
          ...site,
          clientName: client?.name || null
        };
      });
      setSites(enrichedSites);

      // Enrichir les comptes avec leurs associations aux sites
      const enrichedAccounts = flatAccounts.map(account => {
        /**
         * Le test d'appartenance, selon la plateforme — la règle est partagée avec le périmètre
         * du client filtré (`predicatDeRattachement`). Matomo et Piano passent par le résolveur,
         * qui refuse un numéro nu ambigu au lieu de retenir le premier site venu ; les autres
         * plateformes comparent leurs identités, tirets normalisés des deux côtés.
         */
        const correspond = predicatDeRattachement(account, resolveurs);

        const associatedSite = enrichedSites.find(site =>
          (site.accountIds || []).some(correspond)
        );

        return {
          ...account,
          siteId: associatedSite?.id || null,
          siteName: associatedSite?.name || null
        };
      });
      setAccounts(enrichedAccounts);

      // Enrichir les clients avec le compte des sites et des accès
      const enrichedClients = clientsData.map(client => {
        const clientSites = enrichedSites.filter(site => site.clientId === client.id);
        return {
          ...client,
          sitesCount: clientSites.length,
          sites: clientSites,
          // accessCount vient de _count.clientEmails du backend
          accessCount: client._count?.clientEmails || client.clientEmails?.length || 0
        };
      });
      setClients(enrichedClients);

    } catch (err) {
      console.error('[KANBAN] Erreur chargement données:', err);
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Associer un compte à un site
  /**
   * `compteVise` : le compte lui-même, pour rafraîchir la carte après l'appel.
   *
   * L'état local se mettait à jour en comparant `a.id === accountId`, or `accountId` est
   * l'identifiant **écrit dans le site** — `matomo-{connexion}-{numéro}` — tandis que `a.id` est
   * l'identifiant de base du compte. Les deux ne coïncident jamais pour Matomo et Piano : la
   * carte gardait donc son étiquette de site alors que le serveur avait bien enregistré, et le
   * geste semblait sans effet jusqu'au rechargement complet de la page.
   */
  const associateAccountToSite = useCallback(async (accountId, siteId, remplaces = [], compteVise = null) => {
    try {
      const site = sites.find(s => s.id === siteId);
      if (!site) throw new Error('Site non trouvé');

      // Les écritures anciennes que ce geste rend caduques — un numéro de site nu, qui ne disait
      // pas de quelle instance il s'agissait. Les garder à côté laisserait l'ambiguïté en place.
      const aRetirer = new Set(remplaces.map(String));
      const conserves = (site.accountIds || []).filter(id => !aRetirer.has(String(id)));
      const newAccountIds = conserves.includes(accountId) ? conserves : [...conserves, accountId];

      await axios.put(
        `${API_URL}/api/sites/${siteId}`,
        { accountIds: newAccountIds },
        authHeaders
      );

      // Mettre à jour le state local
      setSites(prev => prev.map(s =>
        s.id === siteId ? { ...s, accountIds: newAccountIds } : s
      ));

      // Le compte visé prime ; la comparaison par identifiant ne sert que de repli, pour un
      // appelant qui ne le passerait pas.
      const estLeCompte = (a) => (compteVise
        ? a.id === compteVise.id && a.provider === compteVise.provider
        : (a.id === accountId || a.sourceId === accountId));

      setAccounts(prev => prev.map(a =>
        estLeCompte(a) ? { ...a, siteId, siteName: site.name } : a
      ));

      return true;
    } catch (err) {
      console.error('[KANBAN] Erreur association compte-site:', err);
      throw err;
    }
  }, [sites, token]);

  // Dissocier un compte d'un site
  /** Même raison que pour l'association : voir le commentaire de `associateAccountToSite`. */
  const dissociateAccountFromSite = useCallback(async (accountId, siteId, remplaces = [], compteVise = null) => {
    try {
      const site = sites.find(s => s.id === siteId);
      if (!site) throw new Error('Site non trouvé');

      // `remplaces` porte les écritures anciennes qui liaient ce compte : sans elles, défaire un
      // rattachement au format récent laisserait le numéro nu en place, et le lien persisterait.
      const aRetirer = new Set([String(accountId), ...remplaces.map(String)]);
      const newAccountIds = (site.accountIds || []).filter(id => !aRetirer.has(String(id)));

      await axios.put(
        `${API_URL}/api/sites/${siteId}`,
        { accountIds: newAccountIds },
        authHeaders
      );

      // Mettre à jour le state local
      setSites(prev => prev.map(s =>
        s.id === siteId ? { ...s, accountIds: newAccountIds } : s
      ));

      const estLeCompte = (a) => (compteVise
        ? a.id === compteVise.id && a.provider === compteVise.provider
        : (a.id === accountId || a.sourceId === accountId));

      setAccounts(prev => prev.map(a =>
        estLeCompte(a) ? { ...a, siteId: null, siteName: null } : a
      ));

      return true;
    } catch (err) {
      console.error('[KANBAN] Erreur dissociation compte-site:', err);
      throw err;
    }
  }, [sites, token]);

  // Assigner un site à un client
  const assignSiteToClient = useCallback(async (siteId, clientId) => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (!client) throw new Error('Client non trouvé');

      await axios.put(
        `${API_URL}/api/sites/${siteId}`,
        { clientId },
        authHeaders
      );

      // Mettre à jour le state local
      setSites(prev => prev.map(s =>
        s.id === siteId ? { ...s, clientId, clientName: client.name } : s
      ));

      // Mettre à jour le compteur de sites du client
      setClients(prev => prev.map(c => {
        if (c.id === clientId) {
          const updatedSites = [...(c.sites || []), sites.find(s => s.id === siteId)];
          return { ...c, sitesCount: updatedSites.length, sites: updatedSites };
        }
        return c;
      }));

      return true;
    } catch (err) {
      console.error('[KANBAN] Erreur assignation site-client:', err);
      throw err;
    }
  }, [clients, sites, token]);

  // Retirer un site d'un client
  const unassignSiteFromClient = useCallback(async (siteId) => {
    try {
      const site = sites.find(s => s.id === siteId);
      const previousClientId = site?.clientId;

      await axios.put(
        `${API_URL}/api/sites/${siteId}`,
        { clientId: null },
        authHeaders
      );

      // Mettre à jour le state local
      setSites(prev => prev.map(s =>
        s.id === siteId ? { ...s, clientId: null, clientName: null } : s
      ));

      // Mettre à jour le compteur de sites du client précédent
      if (previousClientId) {
        setClients(prev => prev.map(c => {
          if (c.id === previousClientId) {
            const updatedSites = (c.sites || []).filter(s => s.id !== siteId);
            return { ...c, sitesCount: updatedSites.length, sites: updatedSites };
          }
          return c;
        }));
      }

      return true;
    } catch (err) {
      console.error('[KANBAN] Erreur retrait site-client:', err);
      throw err;
    }
  }, [sites, token]);

  // Créer un site
  const createSite = useCallback(async (data) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/sites`,
        data,
        authHeaders
      );

      const newSite = response.data?.site || response.data;
      setSites(prev => [...prev, newSite]);

      return newSite;
    } catch (err) {
      console.error('[KANBAN] Erreur création site:', err);
      throw err;
    }
  }, [token]);

  // Créer un client
  const createClient = useCallback(async (data) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/client-management/clients`,
        data,
        authHeaders
      );

      const newClient = { ...(response.data?.client || response.data), sitesCount: 0, sites: [] };
      setClients(prev => [...prev, newClient]);

      return newClient;
    } catch (err) {
      console.error('[KANBAN] Erreur création client:', err);
      throw err;
    }
  }, [token]);

  // Mettre à jour la recherche
  const setSearchQuery = useCallback((column, query) => {
    setSearchQueries(prev => ({ ...prev, [column]: query }));
  }, []);

  // Sélectionner un élément pour filtrage en cascade
  const selectItem = useCallback((type, id) => {
    setSelection(prev => {
      // Toggle: si déjà sélectionné, désélectionner
      if (prev.type === type && prev.id === id) {
        return { type: null, id: null };
      }
      return { type, id };
    });
  }, []);

  // Effacer la sélection
  const clearSelection = useCallback(() => {
    setSelection({ type: null, id: null });
  }, []);

  /**
   * Les comptes rattachés à ces sites — par la règle partagée, jamais par une comparaison
   * d'identifiants écrite ici.
   *
   * ⚠️ **Le filtrage en cascade portait sa PROPRE lecture du rattachement**, et elle ne
   * connaissait qu'une forme qualifiée sur deux : `matomo-{connexion}-{numéro}`, écrite en
   * clair. Les comptes **Piano**, rattachés sous `piano-{connexion}-{numéro}`, n'étaient donc
   * jamais retenus — choisir le client « BZB » n'ouvrait aucun de ses deux comptes Piano,
   * alors que choisir le connecteur Piano ouvrait bien BZB. Le défaut était muet dans le seul
   * sens qui compte : une colonne plus courte se lit comme un client qui ne possède rien.
   *
   * L'enrichissement, lui, employait déjà `predicatDeRattachement` : la carte du compte
   * affichait son site BZB pendant que la cascade le niait. C'est la divergence que
   * `utils/perimetreClient.js` existe pour empêcher — on l'appelle, on ne la recopie pas.
   */
  const comptesRattachesAux = useCallback(
    (sitesVises) => comptesDesSites(accounts, sitesVises, resolveursDeRattachement(accounts)),
    [accounts]
  );

  /**
   * Ce que le client choisi dans l'en-tête laisse voir, colonne par colonne.
   *
   * Le Gestionnaire servait un inventaire : vingt-trois connecteurs, cent vingt-deux comptes,
   * trente et un sites et vingt-huit clients, les mêmes quel que soit le client filtré en
   * en-tête. On ne pouvait donc pas répondre à la question la plus naturelle qu'on lui pose —
   * « de quoi ce client dispose-t-il ? » — sans la reconstituer de tête.
   *
   * La chaîne est celle du filtrage en cascade : client → ses sites → les comptes rattachés à
   * ces sites → les connexions dont ces comptes proviennent.
   *
   * ⚠️ **Trois états, pas deux.** `null` veut dire « aucun client filtré », et ne retire rien —
   * c'est l'état d'origine, et toute colonne reste entière. Un client filtré rend des ensembles,
   * **même vides** : un client sans site ne voit aucun compte, et c'est une réponse.
   *
   * ⚠️ **Un compte peut être rattaché aux sites de deux clients**, d'une même agence. On ne lit
   * donc pas `account.siteId` — l'enrichissement n'en retient qu'**un**, le premier trouvé —
   * mais les rattachements de chaque site du client. Sans cela, un compte partagé disparaîtrait
   * de l'un des deux périmètres, en silence.
   */
  const perimetreDuClient = useMemo(
    () => calculerPerimetreDuClient({ clientFiltre, sites, comptes: accounts, connecteurs: connectors }),
    [clientFiltre, sites, accounts, connectors]
  );

  /**
   * Le décompte du bandeau de chaque colonne.
   *
   * Il suit le **périmètre**, pas la recherche ni le filtrage en cascade : la pastille dit de
   * quoi la colonne dispose pour ce client, et la recherche creuse à l'intérieur. Sans client
   * filtré, c'est le total d'avant — rien ne change.
   *
   * ⚠️ Les comptes se comptent par `cleDeCarte`, la clé de dédoublonnage de la colonne : deux
   * lignes de bibliothèque Matomo pour un même site n'ouvrent qu'une carte.
   */
  const totauxDeColonne = useMemo(() => {
    if (!perimetreDuClient) {
      return {
        connectors: connectors.length,
        accounts: accounts.length,
        sites: sites.length,
        clients: clients.length
      };
    }
    return {
      connectors: perimetreDuClient.connecteurIds.size,
      accounts: perimetreDuClient.nombreDeCartes,
      sites: perimetreDuClient.siteIds.size,
      clients: perimetreDuClient.clientIds.size
    };
  }, [perimetreDuClient, connectors, accounts, sites, clients]);

  /**
   * Changer de client remet le filtrage en cascade à zéro.
   *
   * Une carte sélectionnée sous un client ne survit pas au suivant : la laisser poserait deux
   * filtres qui ne se recoupent pas, et les quatre colonnes se videraient sans que rien à
   * l'écran n'en donne la raison.
   */
  useEffect(() => {
    setSelection({ type: null, id: null });
  }, [clientFiltre]);

  // Obtenir les IDs liés selon la sélection
  const getRelatedIds = useCallback(() => {
    if (!selection.type || !selection.id) {
      return { connectorIds: null, lignesDeCompte: null, siteIds: null, clientIds: null };
    }

    let relatedSites = [];
    let relatedAccounts = [];
    let relatedClients = [];
    let relatedConnectors = [];

    switch (selection.type) {
      case 'client': {
        // Client sélectionné → sites du client → comptes des sites → connecteurs des comptes
        relatedClients = [selection.id];
        const clientSites = sites.filter(s => s.clientId === selection.id);
        relatedSites = clientSites.map(s => s.id);

        const matchingAccounts = comptesRattachesAux(clientSites);
        relatedAccounts = matchingAccounts.map(cleDeLigne);

        // Connecteurs: trouver les providers des comptes liés
        const accountProviders = [...new Set(matchingAccounts.map(a => a.provider))];
        relatedConnectors = connectors
          .filter(c => accountProviders.includes(c.provider))
          .map(c => c.id);
        break;
      }

      case 'site': {
        // Site sélectionné → client du site, comptes du site, connecteurs des comptes
        const site = sites.find(s => s.id === selection.id);
        relatedSites = [selection.id];
        relatedClients = site?.clientId ? [site.clientId] : [];

        const matchingAccounts = comptesRattachesAux(site ? [site] : []);
        relatedAccounts = matchingAccounts.map(cleDeLigne);

        const accountProviders = [...new Set(matchingAccounts.map(a => a.provider))];
        relatedConnectors = connectors
          .filter(c => accountProviders.includes(c.provider))
          .map(c => c.id);
        break;
      }

      case 'account': {
        /*
         * Compte sélectionné → site du compte, client du site, connecteur du compte.
         *
         * La carte envoie `account.id || account.sourceId` (voir `KanbanView`), et `id` porte
         * l'identifiant de base y compris pour Matomo et Piano : la forme qualifiée n'est
         * jamais une sélection, inutile de la chercher ici.
         */
        const account = accounts.find(a =>
          a.id === selection.id ||
          a.sourceId === selection.id ||
          a.databaseId === selection.id ||
          a.libraryId === selection.id
        );
        relatedAccounts = account ? [cleDeLigne(account)] : [];

        if (account?.siteId) {
          relatedSites = [account.siteId];
          const site = sites.find(s => s.id === account.siteId);
          relatedClients = site?.clientId ? [site.clientId] : [];
        }

        relatedConnectors = connectors
          .filter(c => c.provider === account?.provider)
          .map(c => c.id);
        break;
      }

      case 'connector': {
        // Connecteur sélectionné → comptes du provider → sites des comptes → clients des sites
        const connector = connectors.find(c => c.id === selection.id);
        relatedConnectors = [selection.id];

        /*
         * Le fournisseur, et non la connexion : une carte de connecteur groupe les branchements
         * d'une même plateforme et n'envoie que l'identifiant du premier (voir `KanbanView`).
         * Filtrer par connexion n'ouvrirait donc qu'une part des comptes que la carte annonce.
         */
        const comptesDuConnecteur = accounts.filter(a => a.provider === connector?.provider);
        relatedAccounts = comptesDuConnecteur.map(cleDeLigne);

        const accountSiteIds = comptesDuConnecteur
          .filter(a => a.siteId)
          .map(a => a.siteId);
        relatedSites = [...new Set(accountSiteIds)];

        const siteClientIds = sites
          .filter(s => relatedSites.includes(s.id) && s.clientId)
          .map(s => s.clientId);
        relatedClients = [...new Set(siteClientIds)];
        break;
      }

      default:
        break;
    }

    // Si une sélection est active, toujours retourner les arrays (même vides)
    // pour que le filtrage soit appliqué
    const hasSelection = selection.type && selection.id;
    return {
      connectorIds: hasSelection ? relatedConnectors : null,
      // Des clés de LIGNE, jamais des identifiants bruts : voir `cleDeLigne`.
      lignesDeCompte: hasSelection ? new Set(relatedAccounts) : null,
      siteIds: hasSelection ? relatedSites : null,
      clientIds: hasSelection ? relatedClients : null
    };
  }, [selection, connectors, accounts, sites, comptesRattachesAux]);

  // Filtrer les éléments par périmètre client, recherche ET sélection
  const getFilteredItems = useCallback((column) => {
    const query = searchQueries[column]?.toLowerCase() || '';
    const related = getRelatedIds();
    const perimetre = perimetreDuClient;

    switch (column) {
      case 'connectors': {
        let result = connectors.filter(c =>
          c.connectionName?.toLowerCase().includes(query) ||
          c.provider?.toLowerCase().includes(query) ||
          // La clé technique ne suffit pas : on cherche « chat », pas « OPENAI_ADS ».
          libellePlateforme(c.provider)?.toLowerCase().includes(query) ||
          c.connectorEmail?.toLowerCase().includes(query)
        );
        if (perimetre) {
          result = result.filter(c => perimetre.connecteurIds.has(c.id));
        }
        if (related.connectorIds) {
          result = result.filter(c => related.connectorIds.includes(c.id));
        }
        return result;
      }
      case 'accounts': {
        /*
         * Le nom seul ne suffisait pas.
         *
         * Un compte ChatGPT Ads s'appelle « Adspring (adacct_6a90578c…) » : chercher « chat »
         * ne rendait rien, alors que la plateforme est la première chose qu'on lit sur sa ligne.
         * L'identifiant non plus n'était pas cherché, alors qu'il est souvent ce qu'on a sous la
         * main quand on vient d'une régie.
         */
        let result = accounts.filter(a =>
          a.name?.toLowerCase().includes(query) ||
          String(a.sourceId || '').toLowerCase().includes(query) ||
          libellePlateforme(a.provider || a.type)?.toLowerCase().includes(query)
        );
        if (perimetre) {
          result = result.filter(a => perimetre.lignes.has(cleDeLigne(a)));
        }
        if (related.lignesDeCompte) {
          result = result.filter(a => related.lignesDeCompte.has(cleDeLigne(a)));
        }
        // Dédupliquer par sourceId (évite les doublons Matomo/Piano)
        const seen = new Set();
        result = result.filter(a => {
          const key = cleDeCarte(a);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        return result;
      }
      case 'sites': {
        let result = sites.filter(s =>
          s.name?.toLowerCase().includes(query) ||
          s.description?.toLowerCase().includes(query)
        );
        if (perimetre) {
          result = result.filter(s => perimetre.siteIds.has(s.id));
        }
        if (related.siteIds) {
          result = result.filter(s => related.siteIds.includes(s.id));
        }
        return result;
      }
      case 'clients': {
        let result = clients.filter(c =>
          c.name?.toLowerCase().includes(query) ||
          c.description?.toLowerCase().includes(query)
        );
        if (perimetre) {
          result = result.filter(c => perimetre.clientIds.has(c.id));
        }
        if (related.clientIds) {
          result = result.filter(c => related.clientIds.includes(c.id));
        }
        return result;
      }
      default:
        return [];
    }
  }, [connectors, accounts, sites, clients, searchQueries, getRelatedIds, perimetreDuClient]);

  /**
   * L'identifiant sous lequel un compte doit être **écrit** dans `accountIds`, et les écritures
   * anciennes que cette écriture rend caduques.
   *
   * Bâti sur l'état courant plutôt que dans `loadData` : le glisser-déposer en a besoin, et il
   * n'a pas accès à la fermeture du chargement.
   */
  const rattachement = useCallback((account) => {
    if (!account) return { identifiant: null, remplaces: [] };
    const prefixe = account.provider === 'MATOMO' ? 'matomo'
      : account.provider === 'PIANO_ANALYTICS' ? 'piano'
        : null;
    if (!prefixe || !account.connectionId) {
      return { identifiant: account.id || account.sourceId, remplaces: [] };
    }
    const champs = prefixe === 'matomo' ? ['sourceId', 'idSite'] : ['sourceId', 'siteId'];
    const resolveur = fabriquerResolveurDeRattachement(
      prefixe, accounts.filter(a => a.provider === account.provider), champs
    );
    return {
      identifiant: resolveur.identifiantDe(account),
      caducs: (accountIds) => resolveur.heritesRemplaces(account, accountIds)
    };
  }, [accounts]);

  const value = {
    // Données
    connectors,
    accounts,
    sites,
    clients,

    // États
    loading,
    error,
    searchQueries,
    selection,

    // Le client choisi en en-tête, et ce qu'il laisse voir
    clientFiltre,
    perimetreDuClient,
    totauxDeColonne,

    // Actions
    loadData,
    rattachement,
    associateAccountToSite,
    dissociateAccountFromSite,
    assignSiteToClient,
    unassignSiteFromClient,
    createSite,
    createClient,
    setSearchQuery,
    getFilteredItems,
    selectItem,
    clearSelection,

    // Refresh
    refreshData: loadData
  };

  return (
    <KanbanContext.Provider value={value}>
      {children}
    </KanbanContext.Provider>
  );
};

export default KanbanContext;
